import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Card } from "@/components/ui/card";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { MapPin, Phone, Mail, Clock, Instagram } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import heroContact from "@/assets/hero-products.jpg";

const Contact = () => {
  const infoSection = useScrollAnimation();
  const faqSection = useScrollAnimation();

  const faqs = [
    {
      question: "Hoe lang bestaat Bo Barbers al?",
      answer: "Bo Barbers is opgericht door Bo, de 3e generatie in een echte kappersfamilie met meer dan 25 jaar ervaring in het vak."
    },
    {
      question: "Kan ik zonder afspraak langskomen?",
      answer: "We werken bij voorkeur op afspraak om wachttijden te voorkomen. Je kunt gemakkelijk online boeken via onze website."
    },
    {
      question: "Welke producten gebruiken jullie?",
      answer: "Wij werken met Bo Barbers en DEPOT®, beide premium producten voor mannen verzorging dat traditionele formules combineert met moderne kwaliteit."
    },
    {
      question: "Waar kan ik parkeren?",
      answer: "Parking Kruisstraat is een fijne optie, een parkeergarage binnen 3 minuten lopen. In de zijstraten is het ook nog mogelijk, alleen wat wel duurder."
    },
    {
      question: "Knippen jullie ook dames?",
      answer: "Wij zijn gespecialiseerd in herenmodellen maar bij ons is iedereen welkom. Ben je opzoek naar dat ene kapsel dat net iets extra heeft? Dan kunnen wij je helpen."
    },
    {
      question: "Doen jullie ook kleuringen?",
      answer: "Kleuringen zijn mogelijk bij ons op aanvraag, maar omdat onze focus op het knippen ligt en wij ons graag specialiseren gaat daar de voorkeur naar uit."
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="Contact & Openingstijden | Bo Barbers Utrecht"
        description="Bezoek Bo Barbers aan de Nobelstraat 263 in Utrecht. Openingstijden, telefoon, e-mail en antwoorden op veelgestelde vragen."
        path="/contact"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((f) => ({
              "@type": "Question",
              name: f.question,
              acceptedAnswer: { "@type": "Answer", text: f.answer },
            })),
          },
          {
            "@context": "https://schema.org",
            "@type": "BarberShop",
            name: "Bo Barbers",
            url: "https://bobarbers.nl/contact",
            telephone: "+31307200888",
            email: "info@bobarbers.nl",
            address: {
              "@type": "PostalAddress",
              streetAddress: "Nobelstraat 263",
              postalCode: "3512 EM",
              addressLocality: "Utrecht",
              addressCountry: "NL",
            },
            openingHoursSpecification: [
              { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday","Tuesday","Wednesday","Friday"], opens: "08:30", closes: "17:30" },
              { "@type": "OpeningHoursSpecification", dayOfWeek: "Thursday", opens: "08:30", closes: "19:00" },
              { "@type": "OpeningHoursSpecification", dayOfWeek: "Saturday", opens: "09:00", closes: "15:00" },
            ],
            sameAs: ["https://www.instagram.com/bobarbers.nl/"],
          },
        ]}
      />
      <Header />
      
      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroContact} alt="Bo Barbers Contact" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        <div className="container mx-auto px-4 sm:px-6 relative z-10 py-24 sm:py-28 md:py-32">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-white/70 uppercase tracking-[0.2em] sm:tracking-[0.3em] mb-4 sm:mb-6 text-xs font-semibold animate-fade-in" style={{ animationDelay: "0.2s", animationFillMode: "forwards" }}>
              CONTACT
            </p>
            <h1 style={{ animationDelay: "0.4s", animationFillMode: "forwards" }} className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 sm:mb-8 text-white leading-[0.95] animate-fade-in px-4 xl:text-5xl">
              Bezoek ons
            </h1>
            <div className="w-16 sm:w-24 h-1 bg-white mx-auto mb-6 sm:mb-8 animate-fade-in" style={{ animationDelay: "0.6s", animationFillMode: "forwards" }}></div>
            <p className="text-white/80 max-w-xl mx-auto text-sm sm:text-base leading-relaxed animate-fade-in px-4" style={{ animationDelay: "0.8s", animationFillMode: "forwards" }}>
              Nobelstraat 263, 3512 EM Utrecht
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info & Hours */}
      <section ref={infoSection.ref} className="py-16 sm:py-24 md:py-32 bg-card">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 mb-12 sm:mb-16 transition-all duration-1000 ${infoSection.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              {/* Contact Details */}
              <Card className="p-6 sm:p-8 bg-background border-border">
                <h2 className="font-display text-xl sm:text-2xl font-bold mb-6 sm:mb-8 text-foreground">Contact</h2>
                <div className="space-y-4 sm:space-y-6">
                  <div className="flex items-start gap-3 sm:gap-4">
                    <MapPin className="w-5 h-5 text-foreground/70 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-foreground mb-1 text-sm sm:text-base">Adres</p>
                      <p className="text-foreground/70 text-xs sm:text-sm">Nobelstraat 263</p>
                      <p className="text-foreground/70 text-xs sm:text-sm">3512 EM Utrecht</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 sm:gap-4">
                    <Phone className="w-5 h-5 text-foreground/70 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-foreground mb-1 text-sm sm:text-base">Telefoon</p>
                      <a href="tel:0307200888" className="text-foreground/70 text-xs sm:text-sm hover:text-foreground transition-colors">
                        030 720 0 888
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 sm:gap-4">
                    <Mail className="w-5 h-5 text-foreground/70 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-foreground mb-1 text-sm sm:text-base">Email</p>
                      <a href="mailto:info@bobarbers.nl" className="text-foreground/70 text-xs sm:text-sm hover:text-foreground transition-colors">
                        info@bobarbers.nl
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 sm:gap-4">
                    <Instagram className="w-5 h-5 text-foreground/70 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-foreground mb-1 text-sm sm:text-base">Instagram</p>
                      <a 
                        href="https://www.instagram.com/bobarbers.nl/" 
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-foreground/70 text-xs sm:text-sm hover:text-foreground transition-colors"
                      >
                        @bobarbers.nl
                      </a>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Opening Hours */}
              <Card className="p-6 sm:p-8 bg-background border-border">
                <div className="flex items-center gap-2 sm:gap-3 mb-6 sm:mb-8">
                  <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-foreground" />
                  <h2 className="font-display text-xl sm:text-2xl font-bold text-foreground">Openingstijden</h2>
                </div>
                <div className="space-y-3 sm:space-y-4">
                  <div className="flex justify-between items-center py-2 sm:py-3 border-b border-border">
                    <span className="font-semibold text-foreground text-sm sm:text-base">Maandag - Vrijdag</span>
                    <span className="text-foreground/70 text-sm">08:30 - 17:30</span>
                  </div>
                  <div className="flex justify-between items-center py-2 sm:py-3 border-b border-border">
                    <span className="font-semibold text-foreground text-sm sm:text-base">Donderdag</span>
                    <span className="text-foreground/70 text-sm">08:30 - 19:00</span>
                  </div>
                  <div className="flex justify-between items-center py-2 sm:py-3 border-b border-border">
                    <span className="font-semibold text-foreground text-sm sm:text-base">Zaterdag</span>
                    <span className="text-foreground/70 text-sm">09:00 - 15:00</span>
                  </div>
                  <div className="flex justify-between items-center py-2 sm:py-3">
                    <span className="font-semibold text-foreground text-sm sm:text-base">Zondag</span>
                    <span className="text-foreground/70 text-sm">Gesloten</span>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section ref={faqSection.ref} className="py-16 sm:py-24 md:py-32 bg-card">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mx-auto">
            <div className={`text-center mb-10 sm:mb-12 md:mb-16 transition-all duration-1000 ${faqSection.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <p className="text-foreground/60 uppercase tracking-[0.2em] sm:tracking-[0.3em] mb-3 sm:mb-4 text-xs font-semibold">FAQs</p>
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-foreground px-4">Veelgestelde Vragen</h2>
            </div>

            <Accordion type="single" collapsible className={`w-full transition-all duration-1000 delay-200 ${faqSection.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              {faqs.map((faq, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-border">
                  <AccordionTrigger className="text-left font-semibold hover:text-foreground/70 transition-colors text-sm sm:text-base py-3 sm:py-4">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-foreground/70 leading-relaxed text-sm">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
